import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Box, 
  Container, 
  Grid, 
  Typography, 
  List, 
  ListItem, 
  ListItemIcon,
  ListItemText, 
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  InputAdornment,
  Tooltip,
  Avatar
} from '@mui/material';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import InstagramIcon from '@mui/icons-material/Instagram';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import SendIcon from '@mui/icons-material/Send';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import SecurityIcon from '@mui/icons-material/Security';
import { LOGOS } from '../assets/images/imageConfig';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [logoError, setLogoError] = useState(false);
  
  const footerLinks = [
    { text: 'Home', path: '/' },
    { text: 'Security Services', path: '/services' },
    { text: 'About Us', path: '/about' },
    { text: 'Contact Us', path: '/contact' },
  ];
  
  const handleLogoError = () => {
    setLogoError(true);
  };

  const renderLogo = () => {
    if (logoError) {
      return <SecurityIcon sx={{ fontSize: 36, color: 'primary.main', mr: 1.5 }} />;
    }
    
    return (
      <Avatar 
        src={LOGOS.PRIMARY} 
        alt="SafeNext Logo"
        variant="rounded"
        onError={handleLogoError}
        sx={{ 
          width: 40, 
          height: 40, 
          mr: 1.5,
          bgcolor: 'primary.main' 
        }} 
      />
    );
  };
  
  return (
    <Box 
      component="footer" 
      sx={{ 
        bgcolor: '#0A1929',
        color: 'white',
        boxShadow: '0 -4px 10px rgba(0, 0, 0, 0.1)',
        position: 'relative',
        mt: 'auto',
        pt: 0
      }}
    >
      {/* Top accent bar */}
      <Box 
        sx={{ 
          height: 4, 
          bgcolor: 'primary.main', 
          width: '100%' 
        }} 
      />
      
      {/* Main footer content */}
      <Container maxWidth="lg" sx={{ pt: 8, pb: 4 }}>
        <Grid container spacing={4}>
          {/* Company Info */}
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              {renderLogo()}
              <Typography 
                variant="h4" 
                component="div" 
                fontWeight="bold"
                sx={{ 
                  color: 'white',
                  letterSpacing: '0.5px'
                }}
              >
                SafeNext
              </Typography>
            </Box>
            
            <Typography 
              variant="body2" 
              sx={{ 
                mb: 3, 
                color: 'rgba(255, 255, 255, 0.7)',
                lineHeight: 1.6
              }}
            >
              Your Trusted Security Partner founded in 2025 by Mr. Shikhar Singh, providing professional security services.
            </Typography>
            
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2, 
                mb: 3, 
                bgcolor: 'rgba(255, 255, 255, 0.05)', 
                borderRadius: 2,
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}
            >
              <Typography 
                variant="subtitle2" 
                fontWeight="medium" 
                sx={{ color: 'primary.main', mb: 1 }}
              >
                Subscribe to our newsletter
              </Typography>
              
              <Box 
                component="form" 
                sx={{ 
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <TextField
                  placeholder="Your email"
                  size="small"
                  fullWidth
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'rgba(255, 255, 255, 0.05)',
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.2)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'primary.main',
                      },
                    },
                    '& .MuiInputBase-input': {
                      color: 'white',
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton 
                          size="small" 
                          edge="end" 
                          sx={{ 
                            color: 'primary.main',
                            '&:hover': {
                              bgcolor: 'primary.main',
                              color: 'white'
                            }
                          }}
                        >
                          <SendIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Paper>
            
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              {[
                { icon: <FacebookIcon />, label: 'Facebook', url: 'https://www.facebook.com/login/' },
                { icon: <TwitterIcon />, label: 'Twitter', url: 'https://twitter.com/i/flow/login' },
                { icon: <LinkedInIcon />, label: 'LinkedIn', url: 'https://www.linkedin.com/login' },
                { icon: <InstagramIcon />, label: 'Instagram', url: 'https://www.instagram.com/accounts/login/' }
              ].map((social, index) => (
                <Tooltip key={index} title={social.label} arrow placement="top">
                  <IconButton 
                    aria-label={social.label}
                    component="a"
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer" 
                    sx={{ 
                      color: 'white', 
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                      '&:hover': { 
                        bgcolor: 'primary.main',
                      }
                    }}
                    size="small"
                    onClick={(e) => {
                      e.preventDefault();
                      window.open(social.url, '_blank', 'noopener,noreferrer');
                    }}
                  >
                    {social.icon}
                  </IconButton>
                </Tooltip>
              ))}
            </Stack>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography 
              variant="h6" 
              fontWeight="bold" 
              gutterBottom
              sx={{
                position: 'relative',
                display: 'inline-block',
                pb: 1,
                mb: 2,
                '&:after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  width: '40px',
                  height: '2px',
                  bgcolor: 'primary.main'
                }
              }}
            >
              Quick Links
            </Typography>
            
            <List disablePadding>
              {footerLinks.map((item) => (
                <ListItem 
                  key={item.text} 
                  component={Link} 
                  to={item.path} 
                  disablePadding
                  sx={{ 
                    color: 'rgba(255, 255, 255, 0.7)', 
                    textDecoration: 'none', 
                    pb: 1,
                    transition: 'all 0.2s',
                    '&:hover': { 
                      color: 'primary.main',
                      transform: 'translateX(5px)'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <KeyboardArrowRightIcon 
                      fontSize="small" 
                      sx={{ color: 'primary.main' }} 
                    />
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.text} 
                    primaryTypographyProps={{ 
                      variant: 'body2',
                      fontWeight: 'medium'
                    }} 
                  />
                </ListItem>
              ))}
            </List>
          </Grid>

          {/* Contact Info */}
          <Grid item xs={12} sm={6} md={5}>
            <Typography 
              variant="h6" 
              fontWeight="bold" 
              gutterBottom
              sx={{
                position: 'relative',
                display: 'inline-block',
                pb: 1,
                mb: 2,
                '&:after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  width: '40px',
                  height: '2px',
                  bgcolor: 'primary.main'
                }
              }}
            >
              Contact Us
            </Typography>
            
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2.5, 
                bgcolor: 'rgba(255, 255, 255, 0.05)', 
                borderRadius: 2,
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}
            >
              <List disablePadding>
                <ListItem disablePadding sx={{ mb: 2 }}>
                  <ListItemIcon 
                    sx={{ 
                      minWidth: 40, 
                      alignSelf: 'flex-start',
                      mt: 0.5
                    }}
                  >
                    <Box 
                      sx={{ 
                        bgcolor: 'rgba(255, 255, 255, 0.1)', 
                        borderRadius: '50%',
                        width: 32,
                        height: 32,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <LocationOnIcon fontSize="small" sx={{ color: 'primary.main' }} />
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 'medium', mb: 0.5 }}>
                        Address
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        Pune, Maharashtra, India
                      </Typography>
                    }
                  />
                </ListItem>
                
                <ListItem disablePadding sx={{ mb: 2 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Box 
                      sx={{ 
                        bgcolor: 'rgba(255, 255, 255, 0.1)', 
                        borderRadius: '50%',
                        width: 32,
                        height: 32,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <PhoneIcon fontSize="small" sx={{ color: 'primary.main' }} />
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 'medium', mb: 0.5 }}>
                        Phone
                      </Typography>
                    }
                    secondary={
                      <Typography 
                        variant="body2" 
                        component="a" 
                        href="tel:+917668888811" 
                        sx={{ 
                          color: 'rgba(255, 255, 255, 0.7)',
                          textDecoration: 'none',
                          '&:hover': {
                            color: 'primary.main'
                          }
                        }}
                      >
                        +91 7668888811
                      </Typography>
                    }
                  />
                </ListItem>
                
                <ListItem disablePadding>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Box 
                      sx={{ 
                        bgcolor: 'rgba(255, 255, 255, 0.1)', 
                        borderRadius: '50%',
                        width: 32,
                        height: 32,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <EmailIcon fontSize="small" sx={{ color: 'primary.main' }} />
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 'medium', mb: 0.5 }}>
                        Email
                      </Typography>
                    }
                    secondary={
                      <Typography 
                        variant="body2" 
                        component="a" 
                        href="mailto:shikhar482006@gmail.com" 
                        sx={{ 
                          color: 'rgba(255, 255, 255, 0.7)',
                          textDecoration: 'none',
                          '&:hover': {
                            color: 'primary.main'
                          }
                        }}
                      >
                        shikhar482006@gmail.com
                      </Typography>
                    }
                  />
                </ListItem>
              </List>
            </Paper>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
        
        <Grid container alignItems="center">
          <Grid item xs={12}>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.6)',
                textAlign: 'center'
              }}
            >
              &copy; {currentYear} SafeNext. All rights reserved.
            </Typography>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Footer; 